import { useParams } from "#src/Routers/hooks";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { useTableSortingAndPagination } from "#src/components/Redux/reducers/tableStateReducer";
import { useListEvents } from "#src/components/hooks/adapters/useEvents";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import { EventCategoryContext } from "#src/contexts/EventCategoryContext";
import { EventTemplateContext } from "#src/contexts/EventTemplateContext";
import useLocalization from "#src/hooks/useLocalization";
import { linkToEventDetails } from "#src/routes/events/event-categories/[eventCategoryId]/templates/[templateId]/events/[eventId]";
import { linkToFacilityDetail } from "#src/routes/organization/facilities/[facilityId]";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  DataTable,
  DataTablePanel,
  Dialog,
  HeaderType,
  Icon,
  Pill,
  PillProps,
  StorageKeys,
  useFilters,
  useToast,
} from "@validereinc/common-components";
import {
  EventAdapter,
  EventGetListRequestType,
  EventStatus,
  EventType,
  FacilityAdapter,
  SortDirection,
} from "@validereinc/domain";
import { toTitleCaseString } from "@validereinc/utilities";
import pLimit from "p-limit";
import React, { useContext, useState } from "react";

export const statusPillMapping: Record<
  keyof typeof EventStatus,
  PillProps["variant"]
> = {
  open: "warning",
  overdue: "error",
  dismissed: "default",
  resolved: "success",
};

export const EventsTablePanel = ({
  filterConfigStorageKey,
  tableConfigStorageKey,
  tableStateStorageKey,
}: Pick<
  StorageKeys,
  "filterConfigStorageKey" | "tableConfigStorageKey" | "tableStateStorageKey"
>) => {
  const { eventCategory } = useContext(EventCategoryContext) || {};
  const { eventTemplate } = useContext(EventTemplateContext) || {};
  const { localize } = useLocalization();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { eventCategoryId, eventTemplateId } = useParams<{
    eventCategoryId: string;
    eventTemplateId: string;
  }>();
  const [filters] = useFilters<
    EventGetListRequestType & { associated_facility?: { id: string } }
  >(filterConfigStorageKey);
  const [selectedRows, setSelectedRows] = useState<Record<string, EventType>>(
    {}
  );
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [tableState, setTableState] = useTableSortingAndPagination(
    {
      sortBy: "created_at",
      sortDirection: SortDirection.DESCENDING,
    },
    filters,
    tableStateStorageKey
  );

  const reqParams: EventGetListRequestType = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    meta: { eventSchema: true },
    filters: {
      ...filters,
      ...(filters.associated_facility
        ? { "associated_facility.id": (filters.associated_facility as any)?.id }
        : {}),
      ...(eventCategory || eventCategoryId
        ? { event_category_id: eventCategory?.id || eventCategoryId }
        : {}),
      ...(eventTemplate || eventTemplateId
        ? { event_schema_id: eventTemplate?.id || eventTemplateId }
        : {}),
    },
  };

  const { data: events, isLoading } = useListEvents(reqParams);
  // REVIEW: this sort of asset relationship is slated to be removed. Remove
  // when BE support is dropped and/or BE migrations have completed.
  const eventsFacilitiesLegacyQueries = useQueries({
    queries:
      events?.data.map((event) => ({
        queryKey: ["facilities", { filter: { "event.id": event.id } }],
        queryFn: () =>
          FacilityAdapter.getList({
            page: 1,
            pageSize: 1,
            filters: {
              "event.id": event.id,
            },
          }),
        enabled: !event.associated_facility_id,
      })) ?? [],
  });

  const canDeleteEvents = useIsFeatureAvailable({
    featureFlagQuery: "core:events",
    permissionQuery: "events:write",
  });

  const deleteMutation = useMutation({
    mutationFn: async (eventId: string) => {
      await EventAdapter.deleteOne({ id: eventId });
    },
  });

  const headers: Array<HeaderType<EventType>> = [
    {
      key: "name",
      label: "Event Name",
      isSortable: true,
      renderComponent: ({ item }) => {
        const categoryId =
          (item as any)?.event_schema?.event_category_id ?? eventCategory?.id;
        const schemaId = (item as any)?.event_schema_id ?? eventTemplate?.id;
        const { name, id } = item;
        return categoryId && schemaId && name && id ? (
          <RoutingLink to={linkToEventDetails(categoryId, schemaId, id)}>
            {name}
          </RoutingLink>
        ) : (
          "-"
        );
      },
    },
    {
      key: "status",
      label: "Status",
      renderComponent: ({ item }) => (
        <Pill variant={statusPillMapping[item.status]}>
          {toTitleCaseString(item.status, {
            intent: "ui",
            exceptions: ["oil-and-gas"],
          })}
        </Pill>
      ),
    },
    { key: "description", label: "Event Description" },
    {
      key: "associated_facility_id",
      label: `Associated ${localize("Facility")}`,
      renderComponent: ({ item, idx }) => {
        const facilityId =
          item.associated_facility_id ??
          eventsFacilitiesLegacyQueries[idx]?.data?.data[0]?.id;
        const facilityName =
          item.associated_facility?.name ??
          eventsFacilitiesLegacyQueries[idx]?.data?.data[0]?.name;
        return facilityId && facilityName ? (
          <RoutingLink to={linkToFacilityDetail(facilityId)}>
            {facilityName}
          </RoutingLink>
        ) : (
          "-"
        );
      },
    },
    {
      key: "start",
      label: "Event Start",
      isSortable: true,
      renderComponent: ({ item }) => (
        <DataTable.DataRow.DateCell
          value={item.start}
          withTime
        />
      ),
    },
    {
      key: "end",
      label: "Event End",
      isSortable: true,
      renderComponent: ({ item }) => (
        <DataTable.DataRow.DateCell
          value={item.end}
          withTime
        />
      ),
    },
    {
      key: "duration",
      label: "Duration",
      isSortable: true,
    },
    {
      key: "due_date",
      label: "Due Date",
      isSortable: true,
      renderComponent: ({ item }) => (
        <DataTable.DataRow.DateCell
          value={item.due_date}
          withTime
        />
      ),
    },
    {
      key: "created_at",
      label: "Date Created",
      isSortable: true,
      renderComponent: ({ item }) => (
        <DataTable.DataRow.DateCell
          value={item.created_at}
          withTime
        />
      ),
    },
  ];

  const handleBulkDelete = async () => {
    const selectedEventIds = Object.keys(selectedRows);
    const limitPromises = pLimit(8);

    setIsDeleteConfirmationOpen(false);

    toast
      .pushAsync(
        Promise.all(
          selectedEventIds.map((eventId) =>
            limitPromises(async () => deleteMutation.mutateAsync(eventId))
          )
        ),
        {
          intent: "note",
          description: `Deleting ${selectedEventIds.length} selected event${selectedEventIds.length === 1 ? "" : "s"}...`,
          isBusy: true,
        },
        {
          isDismissible: false,
          duration: Infinity,
        }
      )
      .then(({ data, toast: firstToast }) => {
        toast.push(
          {
            intent: "success",
            description: `Successfully deleted ${data.length} event${data.length === 1 ? "" : "s"}`,
          },
          {
            id: firstToast.id,
            isDismissible: true,
            duration: 5000,
          }
        );
        setSelectedRows({});
      })
      .catch(({ error, toastId }) => {
        console.error("Error deleting events", error);
        toast.push(
          {
            intent: "error",
            description: "Failed to delete selected events. Please try again.",
          },
          {
            id: toastId,
            isDismissible: true,
            duration: 5000,
          }
        );
      })
      .finally(() => {
        queryClient.invalidateQueries({
          queryKey: ["events"],
        });
      });
  };

  return (
    <>
      <DataTablePanel
        storageKey={tableConfigStorageKey}
        panelProps={{
          title: "Events",
        }}
        dataTableProps={{
          items: events?.data ?? [],
          headers,
          isLoading,
          getItemId: (item) => item.id,
          selected: selectedRows,
          onSelectionChange: setSelectedRows,
          getItemBulkActions: canDeleteEvents
            ? ({ buttonProps }) => [
                <Button
                  key="delete"
                  {...buttonProps}
                  variant="error-outline"
                  slotLeft={<Icon variant="trash" />}
                  onClick={() => setIsDeleteConfirmationOpen(true)}
                >
                  Delete
                </Button>,
              ]
            : undefined,
          pagination: {
            page: tableState.page,
            pageSize: tableState.pageSize,
            total: events?.total_entries,
            pageSizeText: tableState.pageSizeText,
          },
          sorting: {
            sortBy: tableState.sortBy,
            sortDirection: tableState.sortDirection,
          },
          onPaginationChange: setTableState,
          onSortChange: setTableState,
        }}
      />

      {canDeleteEvents && (
        <Dialog
          title="Delete Events?"
          isOpen={isDeleteConfirmationOpen}
          onClose={() => setIsDeleteConfirmationOpen(false)}
          actionRow={[
            <Button
              key="submit"
              variant="error"
              isLoading={deleteMutation.isLoading}
              onClick={handleBulkDelete}
            >
              Delete
            </Button>,
          ]}
        >
          You&apos;re deleting {Object.keys(selectedRows).length} event
          {Object.keys(selectedRows).length === 1 ? "" : "s"}. This action
          cannot be undone and will remove all associated data including form
          submissions, alerts, and equipment associations. Are you sure you want
          to continue?
        </Dialog>
      )}
    </>
  );
};
